import { useCallback } from "react";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";
import { useVideoAnalyticsContext } from "../../contexts/VideoAnalyticsContext";
import { VideoEvent } from "../../../lib/analytics/utils";
import { useOrcaAdsContext } from "../../contexts/OrcaAdsContext";
import { usePromoContext } from "../../contexts/PromoContext";
import { AutoPlayType, PromoState } from "../../../globals";
import { useOrcaCommonContext } from "../../contexts/OrcaCommonContext";
import { useAutoplayContext } from "../../contexts/AutoplayContext";
import {
  autoplayStateAllowsAds,
  setNoAdsCookie,
  videoHasAds
} from "../../ads/utils/helpers";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import { useHlsContext } from "../../contexts/HlsContext";
import { useStickyVideoContext } from "../../contexts/StickyVideoContext";

/**
 * Custom hook to handle the start of a video playback.
 * It manages the video element, checks for ads, and handles autoplay behavior.
 * It also sets up the necessary context and analytics for the video playback.
 * @param {Function} transitionToVideo
 * @param {Object} config
 * @param {Function} setupSubtitlesListener
 * @returns
 */
export const useHandleVideoStart = (
  transitionToVideo,
  config,
  setupSubtitlesListener
) => {
  const videoElementRef = useVideoElementRefContext();
  const { sendAnalyticsEvent } = useVideoAnalyticsContext();
  const { shouldShowAds, transitionToAd } = useOrcaAdsContext();
  const { setPromoState } = usePromoContext();
  const { setRenderControls, setInitialMuteState } = useOrcaCommonContext();
  const { autoplayState } = useAutoplayContext();
  const { videoData, uniqueId, videoId } = useVideoDataContext();
  const { setUpVideoSource } = useHlsContext();
  const { isStickyEnabled, updateStickyContext, setOverrideStickyContext } =
    useStickyVideoContext();

  const { isSinglePrerollVideo = false } = config;

  const processAdPlaybackBasedOnAutoplayStatus = useCallback(
    ({ hasAds, isAutoplay, forcePlayAd }) => {
      if (isAutoplay && !forcePlayAd) {
        videoElementRef.current.autoplay = true;
      }

      if (hasAds && !config?.adOverrides?.skipAd) {
        const fallback = () => {
          sendAnalyticsEvent(VideoEvent.START, {
            videostart_id: uniqueId
          });
          transitionToVideo({ isAutoplay });
        };
        transitionToAd(fallback);
      } else {
        transitionToVideo({ isAutoplay });
        if (forcePlayAd) {
          setPromoState(PromoState.HIDDEN);
          setRenderControls(
            autoplayState.type !== AutoPlayType.LOOP_NO_CONTROLS
          );
        }
      }
    },
    [
      videoElementRef,
      transitionToAd,
      sendAnalyticsEvent,
      transitionToVideo,
      setPromoState,
      setRenderControls,
      autoplayState.type,
      uniqueId,
      config?.adOverrides?.skipAd
    ]
  );

  const handleStart = useCallback(
    forcePlayAd => {
      const { isAutoplay } = autoplayState;

      const hasAds =
        shouldShowAds &&
        videoHasAds(videoData, config) &&
        (autoplayStateAllowsAds(autoplayState) || forcePlayAd);

      setUpVideoSource();

      setInitialMuteState(forcePlayAd);

      if (isStickyEnabled) {
        updateStickyContext({ playingId: uniqueId });
        setOverrideStickyContext(false);
      }

      if (isSinglePrerollVideo) {
        setNoAdsCookie(videoId);
      }

      setupSubtitlesListener();

      processAdPlaybackBasedOnAutoplayStatus({
        hasAds,
        isAutoplay,
        forcePlayAd
      });
    },
    [
      shouldShowAds,
      videoData,
      config,
      autoplayState,
      setUpVideoSource,
      processAdPlaybackBasedOnAutoplayStatus,
      updateStickyContext,
      isStickyEnabled,
      uniqueId,
      setOverrideStickyContext,
      isSinglePrerollVideo,
      videoId,
      setupSubtitlesListener,
      setInitialMuteState
    ]
  );

  return handleStart;
};
